import React,{useEffect} from 'react'
import { buildDynamicGroupKeyValue, destructDynamicGroupKey } from './DocSCFunctions'


const useDynamicGroup = (props) => {
    const { dynamicGroupData, currentKey, 
            onGroupSelected, onGroupRefresh, onSearchByDynamicGroup } = props;

    //this will store the dynamic group key by level
    const [dynamicGroupSelection, setDynamicGroupSelection] = React.useState({});
    // const [dynamicGroupSelection, setDynamicGroupSelection] = React.useState([{groupKey: '', choiceKey: '', choiceLabel: ''}]);
    // const [activeGroupPosition, setActiveGroupPosition] = React.useState(0);

    //this will store the tree structure of the dynamic groups choices received from the server
    const [dynamicGroupChoiceData, setDynamicGroupChoiceData] = React.useState({});
    const [latestActiveNodePath, setLatestActiveNodePath] = React.useState("");

    // console.log('useDynamicGroup state:', { dynamicGroupSelection, dynamicGroupChoiceData });

    const initialiseSelector = () => {
        //initialise the dynamic group key by resetting all state to the initial 
        // selection from the parent
        if(currentKey && dynamicGroupChoiceData && Object.keys(dynamicGroupChoiceData).length === 0) {
            // Destruct the currentKey and set it as initial selection
            const initialSelection = destructDynamicGroupKey(currentKey);
            setDynamicGroupSelection(initialSelection);

            if(initialSelection[0]) {
                onGroupSelect(0, undefined, initialSelection[0]);
            }

            // console.log('Initialized dynamic group selection from currentKey:', initialSelection);
        }
    }

    //ensure state is updated whenever the props are changed by parent
    useEffect(initialiseSelector, [currentKey]);

    const onClearGroupSelection = (skipReload) => {
        setDynamicGroupSelection({});
        setDynamicGroupChoiceData({});
        setLatestActiveNodePath("");
        
        if (!skipReload) {
            onGroupSelect(0, undefined, "", {});
        }
    }

    const getSelectedGroupLabels = () => {
        const labels = [];
        
        // Get all levels in order (0, 1, 2, etc.)
        const levels = Object.keys(dynamicGroupSelection).sort((a, b) => parseInt(a) - parseInt(b));
        
        for (const level of levels) {
            const groupKey = dynamicGroupSelection[level];
            const groupItem = dynamicGroupData[groupKey];

            labels.push(groupItem.dataList.label);
        }
        
        return labels.join(' > ');
    }

    const getLevel0KeyIndex = () => {
        const groupKey = dynamicGroupSelection[0];   // e.g. "G1"
        if (!groupKey) return -1;

        const keys = Object.keys(dynamicGroupData);  // ["G1","G2","G3",...]
        return keys.indexOf(groupKey);              // 0-based index
    };

    const onSetGroupSelection = (level, groupKey) => {
        setDynamicGroupSelection(prev => ({
            ...prev,
            [level]: groupKey
        }));
    }

    const hasGroupSelectionForLevel = (level) => {
        return dynamicGroupSelection.hasOwnProperty(level);
    }

    const onGroupChoiceChange = (nodePath, level) => {
        // console.log('criteria changed 5', { nodePath, level });
        setLatestActiveNodePath(nodePath || "");

        triggerDrilldown(nodePath, level, 1);
    }

    const triggerDrilldown = (nodePath, level, inRefreshLoop) => {
        const pathArray = nodePath ? nodePath.split(',').map(id => id.trim()) : [];
        
        let groupList = {};
        let currentNode = dynamicGroupChoiceData;
        
        // Navigate through the path and extract choice values
        for (let i = 0; i < pathArray.length; i++) {
            const nodeId = pathArray[i];
            const actualNodeKey = nodeId.split('.').pop();
            const levelGroupKey = dynamicGroupSelection[i];
            groupList[i] = {
                groupKey: levelGroupKey,
                choiceKey: '',
                choiceLabel: ''
            };

            if (levelGroupKey && currentNode[actualNodeKey]) {
                const node = currentNode[actualNodeKey];
                groupList[i].choiceKey = node.dataList.key;
                groupList[i].choiceLabel = node.dataList.label;
                
                // Move to children for next level
                currentNode = node.children || {};
            }
        }
        
        const dynamicGroupKeyValue = buildDynamicGroupKeyValue(groupList);
        // console.log('Final search value:', dynamicGroupKeyValue);
        
        // Trigger the search with the complete selection
        onSearchByDynamicGroup(dynamicGroupKeyValue, nodePath, inRefreshLoop);
    }

    //nodePath should have the whole path including the node that was clicked
    const onGroupSelect = (level, nodePath, selectedGroupKey, newGroupSelection) => {
        let newDynamicGroupSelection = { ...(newGroupSelection == null || newGroupSelection === undefined ? dynamicGroupSelection : newGroupSelection) };

        let groupKeyToUse = selectedGroupKey || newDynamicGroupSelection[level];

        if(groupKeyToUse === undefined || groupKeyToUse === null) {
            groupKeyToUse = "";
        }

        if (selectedGroupKey) {
            onSetGroupSelection(level, selectedGroupKey);
            newDynamicGroupSelection[level] = selectedGroupKey;
        }

        if(level === 0) {
            setDynamicGroupChoiceData({});
        }
        
        // console.log('Using group key:', groupKeyToUse, 'for level:', level);
        
        // Build a list of group keys based on the selection path
        // Convert comma-separated string to array for navigation
        const pathArray = nodePath ? nodePath.split(',').map(id => id.trim()) : [];
        
        let groupList = {};
        let currentNode = dynamicGroupChoiceData;
        
        // Navigate through the path and extract choice values
        for (let i = 0; i < pathArray.length; i++) {
            const nodeId = pathArray[i];
            const actualNodeKey = nodeId.split('.').pop();
            const levelGroupKey = newDynamicGroupSelection[i];
            groupList[i] = {
                groupKey: levelGroupKey,
                choiceKey: '',
                choiceLabel: ''
            };

            if (levelGroupKey && currentNode[actualNodeKey]) {
                const node = currentNode[actualNodeKey];
                groupList[i].choiceKey = node.dataList.key;
                groupList[i].choiceLabel = node.dataList.label;
                
                // Move to children for next level
                currentNode = node.children || {};
            }
        }

        if(groupKeyToUse !== "") {
            groupList[level] = {
                groupKey: groupKeyToUse,
                choiceKey: '',
                choiceLabel: ''
            };
        }
        
        // console.log('Group list built from path:', groupList);
        
        // Build the dynamic group key string for server request
        const dynamicGroupKeyValue = buildDynamicGroupKeyValue(groupList);
        // console.log('Dynamic group key value for server:', dynamicGroupKeyValue);
        
        if(onGroupSelected) onGroupSelected(nodePath, dynamicGroupKeyValue);
    }
    
    //this funciton will refresh all the dynamicGroupChoiceData with fresh value from the server
    //if some of the labels are missing from the server as the groups change, 
    // then the default of 0 will be used but the group should remain visible to ensure minium visual change 
    const refreshGroupData = (nodePath) => {
        // console.log('criteria changed 6', nodePath, latestActiveNodePath, dynamicGroupSelection);
        //loop through the dynamicGroupSelection to scan the levels that are loaded, 
        // and then call reload from server for each level that is active
        const firstSelection = Object.keys(dynamicGroupSelection).length > 0 ? Object.keys(dynamicGroupSelection)[0] : "";

        const pathArray = (nodePath || latestActiveNodePath || firstSelection) ? (nodePath || latestActiveNodePath || firstSelection).split(',').map(id => id.trim()) : [];

        let groupList = {};
        let currentNode = dynamicGroupChoiceData;
        
        // Navigate through the path and extract choice values
        for (let i = 0; i < pathArray.length; i++) {
            const nodeId = pathArray[i];
            const actualNodeKey = nodeId.split('.').pop();
            const levelGroupKey = dynamicGroupSelection[i];
            groupList[i] = {
                groupKey: levelGroupKey,
                choiceKey: '',
                choiceLabel: ''
            };

            if (levelGroupKey && currentNode[actualNodeKey]) {
                const node = currentNode[actualNodeKey];
                groupList[i].choiceKey = node.dataList.key;
                groupList[i].choiceLabel = node.dataList.label;
                
                // Move to children for next level
                currentNode = node.children || {};
            }
        }

        let timeoutValue = 0;
        
        //trigger the group search for each level
        for(let gl = 0; gl < pathArray.length; gl++) {
            //build node path from 0 to gl
            const activeNodeArray = pathArray.slice(0, gl + 1);
            let newNodePath = activeNodeArray.join(',');

            const dynamicGroupKeyValue = buildDynamicGroupKeyValue(groupList, gl);
            // console.log('refresh path requests', newNodePath, dynamicGroupKeyValue);

            //add delay in the call so that server gets enough time to process calls in correct sequence
            timeoutValue = gl * 500;

            //get the choices in the curerent level
            setTimeout(() => {
                if(onGroupRefresh) onGroupRefresh(newNodePath, dynamicGroupKeyValue);
            }, timeoutValue);
        }

        // setTimeout(() => {
        //     triggerDrilldown((nodePath || latestActiveNodePath), pathArray.length - 1, 1);
        // }, timeoutValue + 500);
    }
    
    const onGroupChoiceDataRefresh = (nodePath, data) => {
        // console.log("dynamicgroup choice data refresh", { nodePath, data });

        const pathArray = nodePath ? nodePath.split(',').map(id => id.trim()) : [];
        
        //if it is the second level, or first level, update the data to the root
        //as second level means system will send the first level data as well as the second level data
        //in consequent calls
        if(pathArray.length <= 1) {
            onGroupChoiceDataReceived(undefined, data);
        }
        else{
            //now more calls are completing and have data of the 
            const parentNodeArray = pathArray.slice(0, pathArray.length - 1);
            let parentNodePath = parentNodeArray.join(',');

            onGroupChoiceDataReceived(parentNodePath, data);
        }
    }

    const onGroupChoiceDataReceived = (nodePath, data) => {
        console.log("dynamicgroup choice data received", { nodePath, data });

        //before setting the data, ensure that the data is in the expected format
        const newChildrenData = data.viewRows || {};

        // Update the tree structure by injecting children at the right node
        setDynamicGroupChoiceData(prev => {
            // Create a copy of the current tree
            const updatedTree = { ...prev };
            
            if (nodePath === undefined) {
                // Root level - add directly to tree (when parentNodeId is blank)
                Object.assign(updatedTree, newChildrenData);
            } else {
                // Convert comma-separated string to array for navigation
                const pathArray = nodePath ? nodePath.split(',').map(id => id.trim()) : [];
                
                // Use the node path for efficient navigation
                let currentNode = updatedTree;
                
                // Navigate through the path to reach the parent node
                for (let i = 0; i <= pathArray.length - 1; i++) {
                    const pathNodeId = pathArray[i];
                    const actualNodeKey = pathNodeId.split('.').pop();

                    if (currentNode[actualNodeKey]) {
                        //last node in the path is the parent node
                        currentNode = (i === pathArray.length - 1) ? currentNode[actualNodeKey] : currentNode[actualNodeKey].children;
                    } else {
                        console.error('Path navigation failed at:', actualNodeKey);
                        return prev; // Return unchanged if path is invalid
                    }
                }
                
                // Now currentNode should be at the parent level
                const parentNode = currentNode;

                if (parentNode) {
                    parentNode.children = { ...parentNode.children, ...newChildrenData };
                }
            }
            
            return updatedTree;
        });
    }

    const returnObj = {
        onClearGroupSelection,
        onSetGroupSelection,
        hasGroupSelectionForLevel,
        onGroupChoiceDataReceived,
        onGroupChoiceDataRefresh,
        hasDynamicGroupSelection: Object.keys(dynamicGroupSelection).length > 0,
        getSelectedGroupLabels,
        refreshGroupData,
        dynamicGroupData,
        dynamicGroupSelection,
        dynamicGroupChoiceData,
        onGroupSelect,
        onGroupChoiceChange,
        getLevel0KeyIndex,
    }

    return returnObj;
}

export default useDynamicGroup;
